import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Calendar,
  GuestGetBookedMeetingResponseV2,
  GuestGetBookedMeetingResponseV2Interface,
} from '@vendasta/meetings';
import { Observable, of, Subject } from 'rxjs';
import {
  catchError,
  filter,
  map,
  shareReplay,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { BookMeNowStoreService } from '../../book-me-now-store.service';
import { TimeSlot } from '../../bookme/bookme';
import {
  formatDateForDisplay,
  formatDurationFromDates,
  formatNames,
  buildDisplayTitle,
  extractEventTypeName,
  isBatchBooking,
  isInPersonMeeting,
  isVideoMeeting,
} from '../../util';

interface ConfirmChangesMeetingVm {
  meetingId: string;
  eventTypeName: string;
  location: string;
  isInPerson: boolean;
}

interface ConfirmChangesVm {
  title: string;
  displayTitle: string;
  hostNames: string;
  oldTimeSlot: TimeSlot;
  newTimeSlot: TimeSlot;
  oldDateFormatted: string;
  newDateFormatted: string;
  newDurationFormatted: string;
  oldTimezoneZone: string;
  newTimezoneZone: string;
  oldLocation: string;
  newLocation: string;
  isVideo: boolean;
  isInPerson: boolean;
  showLocation: boolean;
  businessLogo: string;
  isBatch: boolean;
  meetings: ConfirmChangesMeetingVm[];
}

@Component({
  selector: 'app-reschedule-confirm-changes',
  templateUrl: './reschedule-confirm-changes.component.html',
  styleUrls: ['./reschedule-confirm-changes.component.scss'],
  standalone: false,
})
export class RescheduleConfirmChangesComponent implements OnInit, OnDestroy {
  calendar$: Observable<Calendar>;
  timezoneOffset$: Observable<string>;
  vm$: Observable<ConfirmChangesVm | null>;
  isTimeSlotChanged: boolean;
  isLocationChanged: boolean;
  isSubmitting = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly bookmenowService: BookMeNowStoreService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    // Check if we have the necessary state data
    const currentState = this.bookmenowService.getCurrentState();
    if (!currentState.selectedTimeSlot) {
      this.router.navigate(['../'], {
        relativeTo: this.route,
        queryParamsHandling: 'preserve',
      });
      return;
    }

    this.calendar$ = this.bookmenowService.calendar$;
    this.timezoneOffset$ = this.bookmenowService.timezoneOffset$;
    this.isLocationChanged = this.bookmenowService.getLocationChanged();
    this.isTimeSlotChanged = this.bookmenowService.getTimeSlotChanged();

    // Build view model from pre-fetched data in store
    this.vm$ = this.bookmenowService.guestBookedMeetingV2$.pipe(
      filter<GuestGetBookedMeetingResponseV2>((info) => Boolean(info)),
      map((meetingInfo) => this.buildViewModel(meetingInfo)),
      shareReplay(1)
    );
  }

  private buildViewModel(
    meetingInfo: GuestGetBookedMeetingResponseV2
  ): ConfirmChangesVm {
    const state = this.bookmenowService.getCurrentState();
    const oldTimeSlot = this.bookmenowService.getOriginalMeetingTimeSlot();
    const newTimeSlot = this.bookmenowService.getSelectedTimeSlot();

    return {
      title: meetingInfo.title || state.calendar?.displayName || '',
      displayTitle: buildDisplayTitle(
        meetingInfo.title || state.calendar?.displayName || '',
        isBatchBooking(meetingInfo as unknown as GuestGetBookedMeetingResponseV2Interface),
        (meetingInfo as unknown as GuestGetBookedMeetingResponseV2Interface).meetings || []
      ),
      hostNames: formatNames(meetingInfo.hostUserNames || []),
      oldTimeSlot,
      newTimeSlot,
      oldDateFormatted: formatDateForDisplay(oldTimeSlot?.start),
      newDateFormatted: formatDateForDisplay(newTimeSlot?.start),
      newDurationFormatted: formatDurationFromDates(
        newTimeSlot?.start,
        newTimeSlot?.end
      ),
      oldTimezoneZone: oldTimeSlot?.timezone?.zone || '',
      newTimezoneZone: newTimeSlot?.timezone?.zone || '',
      oldLocation: meetingInfo.location || '',
      newLocation: state.meetingLocation || '',
      isVideo: isVideoMeeting(meetingInfo.meetingLocationType),
      isInPerson: isInPersonMeeting(meetingInfo.meetingLocationType),
      showLocation: isInPersonMeeting(meetingInfo.meetingLocationType),
      businessLogo:
        meetingInfo.businessLogo || state.calendar?.displayLogoUrl || '',
      ...this.buildBatchFields(meetingInfo),
    } as ConfirmChangesVm;
  }

  private buildBatchFields(meetingInfo: GuestGetBookedMeetingResponseV2): {
    isBatch: boolean;
    isInPerson: boolean;
    showLocation: boolean;
    meetings: ConfirmChangesMeetingVm[];
  } {
    const responseWithBatch = meetingInfo as unknown as GuestGetBookedMeetingResponseV2Interface;
    const isBatch = isBatchBooking(responseWithBatch);
    if (!isBatch) {
      return { isBatch: false, isInPerson: isInPersonMeeting(meetingInfo.meetingLocationType), showLocation: isInPersonMeeting(meetingInfo.meetingLocationType), meetings: [] };
    }
    const meetings = (responseWithBatch.meetings || []).map((m: any) => ({
      meetingId: m.meetingId || '',
      eventTypeName: extractEventTypeName(m.title || ''),
      location: m.location || '',
      isInPerson: isInPersonMeeting(m.meetingLocationType),
    }));
    return {
      isBatch,
      isInPerson: meetings.some((m) => m.isInPerson),
      showLocation: meetings.some((m) => m.isInPerson),
      meetings,
    };
  }

  navigateToGoogleMaps(location: string): void {
    if (location) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(location)}`, '_blank');
    }
  }

  onConfirm(): void {
    if (this.isSubmitting) {
      return;
    }
    this.isSubmitting = true;

    const state = this.bookmenowService.getCurrentState();
    const location = state.meetingLocation;
    const timeSlot = this.bookmenowService.getSelectedTimeSlot();

    const meetingLocation = this.isLocationChanged ? location : undefined;
    const startTime = this.isTimeSlotChanged ? timeSlot?.start : undefined;

    this.bookmenowService
      .rescheduleMeeting({ start: startTime, location: meetingLocation })
      .pipe(
        takeUntil(this.destroy$),
        tap(() => {
          const meetingId = this.route.snapshot.params.meetingId;
          this.router.navigate(['/reschedule-info', meetingId]);
        }),
        catchError((err) => {
          console.error(err);
          this.isSubmitting = false;
          this.bookmenowService.handleMeetingError(err, 'reschedule');
          return of(null);
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onChooseAnotherTime(): void {
    this.bookmenowService.setIsTimeSlotChanged(false);
    this.router.navigate(['../'], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
    });
  }
}
